import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { http } from '@/lib/api';
import ContextualHelp from '@/components/ContextualHelp';
import PageHero from '@/components/PageHero';
import {
  HandHeart, Users, ArrowRight, Search, ShieldAlert, MessageCircleHeart, Phone,
  Baby, Briefcase, Heart, Flame, HeartCrack, Handshake, UserRound, Coffee,
} from 'lucide-react';

const SUPPORT_HELP = {
  whatIs: "A dedicated space for partners, parents, mentors, and friends supporting the men they love. Guides + a Support Coach with the same warmth as the main AI Coach.",
  whyMatters: "Supporting someone you care about through hard seasons is its own skill. This section gives you evidence-informed language and options — so you don't have to guess.",
  howToUse: "Start with the guide closest to your situation (parenting, partner, mentoring, grief). Open the Support Coach when you want to talk through a specific moment.",
  tips: [
    "Listening well is more important than saying the right thing. The guides teach both.",
    "You can't force someone into help. You can leave the door open. That's the work.",
    "Take care of yourself too — burned-out supporters can't hold anyone.",
  ],
};

const ICON_FOR = {
  sup_signs: ShieldAlert, sup_start: MessageCircleHeart, sup_10ways: HandHeart,
  sup_therapy: Users, sup_crisis: Phone, sup_grief: HeartCrack,
  sup_finance: Briefcase, sup_fathers: Baby, sup_young_men: UserRound,
  sup_veterans: Handshake, sup_job_loss: Briefcase, sup_withdraws: Coffee,
  sup_anger: Flame, sup_relationships: Heart,
};

export default function Support() {
  const [guides, setGuides] = useState([]);
  const [query, setQuery] = useState('');
  useEffect(() => { http.get('/support/guides').then(r => setGuides(r.data)); }, []);

  const filtered = guides.filter(g => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (g.title + ' ' + (g.subtitle||'') + ' ' + (g.tags||[]).join(' ')).toLowerCase().includes(q);
  });

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-8 lg:space-y-10" data-testid="support-page">
      <PageHero photo="support" alt="A Brown man smiling with warmth" />
      {/* Hero */}
      <div className="bg-navy text-white rounded-3xl p-8 lg:p-12 card-soft">
        <div className="lg:grid lg:grid-cols-5 lg:items-center lg:gap-10">
          <div className="lg:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Support</p>
            <h1 className="font-heading text-3xl lg:text-5xl mt-2 leading-tight flex items-start gap-2 flex-wrap">Supporting the Men You Care About<ContextualHelp pageTitle="Support" content={SUPPORT_HELP} testid="support-help"/></h1>
            <p className="text-white/70 text-sm lg:text-lg mt-4 lg:leading-relaxed">
              Practical, evidence-informed guidance for partners, parents, family members, mentors, and friends who want to better support the men in their lives.
            </p>
            <Link to="/app/support/coach" data-testid="support-coach-cta"
              className="inline-flex items-center gap-2 mt-6 bg-gold text-white rounded-full px-6 py-3 hover:bg-gold-hover transition-smooth">
              <MessageCircleHeart size={16}/> Talk to the Support Coach
              <ArrowRight size={14}/>
            </Link>
          </div>
          <div className="hidden lg:block lg:col-span-2">
            <HandHeart size={140} strokeWidth={0.8} className="text-gold/40 ml-auto"/>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40"/>
          <input
            value={query}
            onChange={e=>setQuery(e.target.value)}
            data-testid="support-search"
            placeholder="Search guides — anger, therapy, grief, crisis…"
            className="w-full bg-white border border-navy/10 rounded-full pl-11 pr-4 py-3 outline-none focus:ring-2 focus:ring-gold text-sm lg:text-base"
          />
        </div>
        <p className="hidden lg:block text-sm text-navy/50">{filtered.length} guide{filtered.length===1?'':'s'}</p>
      </div>

      {/* Guides grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-testid="support-guides-grid">
        {filtered.map(g => {
          const Icon = ICON_FOR[g.id] || HandHeart;
          return (
            <Link key={g.id} to={`/app/support/${g.id}`} data-testid={`support-guide-${g.id}`}
              className="block bg-white rounded-3xl p-6 lg:p-7 card-soft hover:-translate-y-0.5 transition-smooth flex flex-col h-full lg:min-h-[230px]">
              <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-2xl bg-beige flex items-center justify-center mb-4">
                <Icon className="text-navy" size={20} strokeWidth={1.5}/>
              </div>
              <p className="text-[10px] lg:text-xs uppercase tracking-widest text-gold font-semibold">Guide</p>
              <p className="font-heading text-lg lg:text-xl text-navy leading-snug mt-2">{g.title}</p>
              <p className="text-sm text-navy/60 leading-relaxed mt-2 flex-1">{g.subtitle}</p>
              <p className="text-xs text-navy/40 mt-4 uppercase tracking-widest inline-flex items-center gap-1">Read guide <ArrowRight size={12}/></p>
            </Link>
          );
        })}
      </div>

      <div className="bg-beige rounded-3xl p-6 lg:p-8 card-soft" data-testid="support-crisis-banner">
        <div className="flex items-start gap-4">
          <Phone size={22} className="text-gold shrink-0 mt-1"/>
          <div>
            <p className="font-heading text-lg lg:text-xl">If you&apos;re worried right now</p>
            <p className="text-sm lg:text-base text-navy/70 mt-1">
              Call or text <strong>988</strong> (Suicide & Crisis Lifeline). For veterans, dial 988 then press 1. In immediate danger, call 911 or go to the nearest ER. Read the <Link to="/app/support/sup_crisis" className="underline underline-offset-2 text-navy">crisis guide</Link>.
            </p>
          </div>
        </div>
      </div>

      <p className="text-xs text-navy/40 text-center pt-2">
        Educational and supportive. Not a substitute for therapy, medical care, crisis services, or emergency intervention.
      </p>
    </div>
  );
}
